import React from "react";
import { BRAND_COLORS } from "../../theme/brandTheme";

/* ═══════════════════════════════════════════════════════════════
   Shared theme + chart primitives for the Team Performance page.
   Extracted from TeamPerformancePage so the new chart components in
   TpCharts.jsx can reuse the exact same look without duplication.
   ═══════════════════════════════════════════════════════════════ */

/* Theme – ToBe Esports light brand palette, driven by src/theme/brandTheme */
export const T = {
  bg: BRAND_COLORS.bg,
  card: "rgba(255,255,255,0.96)",
  cardSolid: BRAND_COLORS.surface,
  border: BRAND_COLORS.border,
  borderHover: "rgba(255,16,38,0.55)",
  text: BRAND_COLORS.text,
  muted: BRAND_COLORS.textMuted,
  primary: BRAND_COLORS.primary,
  primaryLight: BRAND_COLORS.primaryLight,
  primaryDark: BRAND_COLORS.primaryDark,
  accent: BRAND_COLORS.primaryLight,
  success: BRAND_COLORS.success,
  danger: BRAND_COLORS.danger,
  warn: BRAND_COLORS.warn,
  glass: "rgba(255,16,38,0.04)",
  glow: "0 6px 28px rgba(255,16,38,0.18)",
  glowSm: "0 4px 16px rgba(255,16,38,0.15)",
  axisStroke: "rgba(255,16,38,0.20)",
  refStroke: "rgba(255,16,38,0.28)",
  tintXs: "rgba(255,16,38,0.03)",
  tintSm: "rgba(255,16,38,0.06)",
  tintMd: "rgba(255,16,38,0.08)",
  tintLg: "rgba(255,16,38,0.10)",
  lineSoft: "rgba(255,16,38,0.06)",
};

export const FONT = "'Kanit', 'Rajdhani', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const CHART_AXIS_TICK = { fill: BRAND_COLORS.chartTick, fontSize: 12, fontWeight: 700, fontFamily: FONT };
export const CHART_LEGEND_STYLE = { color: BRAND_COLORS.chartTick, fontSize: 13, fontFamily: FONT, paddingTop: 10 };
export const CHART_GRID = { strokeDasharray: "2 8", stroke: BRAND_COLORS.chartGrid };

/* ═══════════════════════════════════════════════════════════════
   Custom Recharts Tooltip — enhanced with hero/team display
   ═══════════════════════════════════════════════════════════════ */
export function ChartTooltip({ active, payload, formatter }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  const title = d.hero || d.fullName || d.name || d.label || "Data point";
  const team = d.team || d.teamCode || "";
  const deltaRaw = [d.deltaWR, d.deltaWinRate, d.delta].find((v) => Number.isFinite(Number(v)));
  const deltaValue = Number.isFinite(Number(deltaRaw)) ? Number(deltaRaw) : null;
  const badge = deltaValue === null
    ? null
    : deltaValue >= 8
      ? { label: "MVP", color: T.success, bg: "rgba(0,208,132,0.18)" }
      : deltaValue <= -8
        ? { label: "Risk", color: T.danger, bg: "rgba(255,77,109,0.20)" }
        : { label: "Watch", color: T.warn, bg: "rgba(255,209,102,0.20)" };

  return (
    <div style={{ background: "rgba(255,255,255,0.96)", border: `1.5px solid ${T.border}`, borderRadius: 12, padding: "12px 16px", color: T.text, fontSize: 13, fontFamily: FONT, boxShadow: T.glow, backdropFilter: "blur(14px)", minWidth: 180 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.primaryLight, lineHeight: 1.2 }}>{title}</div>
          {team && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>ทีม {team}</div>}
        </div>
        {badge && (
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: badge.color, background: badge.bg, border: `1px solid ${badge.color}66`, borderRadius: 999, padding: "3px 8px", flexShrink: 0 }}>
            {badge.label}
          </span>
        )}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, padding: "3px 0" }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color || T.primary, display: "inline-block", flexShrink: 0 }} />
          <span style={{ color: T.muted, flex: 1 }}>{p.name}</span>
          <strong style={{ marginLeft: 8 }}>{formatter ? formatter(p.value, p.name, p) : (typeof p.value === "number" ? p.value.toFixed(2) : p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

export function ChartPanel({ children, minHeight = 260 }) {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: "12px 14px 10px",
        background:
          "radial-gradient(circle at 12% 12%, rgba(255,16,38,0.10), transparent 45%), radial-gradient(circle at 88% 6%, rgba(255,16,38,0.14), transparent 40%), linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,244,246,0.96))",
        boxShadow: "inset 0 1px 0 rgba(255,16,38,0.08), 0 14px 34px rgba(255,16,38,0.16), 0 0 0 1px rgba(255,16,38,0.08)",
        minHeight,
      }}
    >
      {children}
    </div>
  );
}

export function EmptyChart({ label = "ยังไม่มีข้อมูลตามเงื่อนไข" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: T.muted, fontSize: 13, flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 28, opacity: 0.4 }}>📊</span>{label}
    </div>
  );
}
